import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Axios } from "../../../Api/axios";
import { ADMIN_LECTURE } from "../../../Api/Api";
import "./edit-lesson.css";

/* ─── URL validators ─── */
const YOUTUBE_RE = /^https?:\/\/(www\.|m\.)?youtube\.com\/(watch|embed|shorts)|^https?:\/\/youtu\.be\//i;
const DRIVE_RE   = /^https?:\/\/(drive\.google\.com|docs\.google\.com)\//i;

function validateVideoUrl(url) {
  if (!url) return "";
  return YOUTUBE_RE.test(url) ? "" : "رابط الفيديو يجب أن يكون من YouTube (youtube.com أو youtu.be)";
}

function validateFileUrl(url, label) {
  if (!url) return "";
  return DRIVE_RE.test(url) ? "" : `رابط ${label} يجب أن يكون من Google Drive`;
}

/* ─── FileSlot component ─── */
function FileSlot({ icon, label, color, accentColor, current, currentIsUrl, newFile, newUrl, urlError,
                    onNewFile, onNewUrl, onClear, fileInputRef }) {
  const hasAnything = !!(current || newFile || newUrl);

  return (
    <div className={`el-slot el-slot-${color}`}>
      <div className="el-slot-header">
        <span className="el-slot-icon">{icon}</span>
        <span className="el-slot-label">{label}</span>
        <span className="el-slot-badge">اختياري</span>
      </div>

      {/* existing file from server */}
      {current && !newFile && (
        <div className={`el-file-tag ${currentIsUrl ? "el-tag-url" : "el-tag-file"}`}>
          <div className="el-file-tag-info">
            {currentIsUrl ? (
              <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
              </svg>
            ) : (
              <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16c0 1.1.89 2 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
              </svg>
            )}
            <span className="el-file-tag-name" title={current}>{current}</span>
          </div>
          <button type="button" className="el-tag-remove" onClick={onClear} title="حذف">
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
            حذف
          </button>
        </div>
      )}

      {/* newly selected file */}
      {newFile && (
        <div className="el-file-tag el-tag-new">
          <div className="el-file-tag-info">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" style={{ color: accentColor }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16c0 1.1.89 2 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
            </svg>
            <div>
              <span className="el-file-tag-name">{newFile.name}</span>
              <span className="el-file-size">
                {newFile.size < 900 * 1024
                  ? (newFile.size / 1024).toFixed(1) + " KB"
                  : (newFile.size / (1024 * 1024)).toFixed(2) + " MB"}
              </span>
            </div>
          </div>
          <button type="button" className="el-tag-remove el-tag-cancel"
            onClick={() => { onNewFile(null); if (fileInputRef?.current) fileInputRef.current.value = ""; }}>
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
            إلغاء
          </button>
        </div>
      )}

      {/* upload zone — show when nothing exists */}
      {!hasAnything && (
        <div className="el-upload-zone" onClick={() => fileInputRef?.current?.click()}>
          <span className="el-upload-icon">{icon}</span>
          <p className="el-upload-hint">اضغط لرفع ملف</p>
        </div>
      )}

      {/* URL input — show when no file */}
      {!newFile && (
        <div className="el-url-wrap">
          <div className={`el-url-input-box ${urlError ? "el-url-error" : newUrl ? "el-url-filled" : ""}`}>
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" className="el-url-icon">
              <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
            </svg>
            <input
              type="url"
              className="el-url-input"
              placeholder="أو أدخل رابط URL..."
              value={newUrl}
              onChange={(e) => onNewUrl(e.target.value)}
            />
            {newUrl && (
              <button type="button" className="el-url-clear-btn" onClick={() => onNewUrl("")}>✕</button>
            )}
          </div>
          {urlError && <p className="el-url-err-msg">{urlError}</p>}
        </div>
      )}

      {/* replace / upload actions */}
      {hasAnything && (
        <div className="el-actions-row">
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files[0] || null;
              if (f) { onNewFile(f); onNewUrl(""); }
            }}
          />
          {!newFile && (
            <button type="button" className="el-action-btn" onClick={() => fileInputRef?.current?.click()}>
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
              </svg>
              استبدال بملف
            </button>
          )}
        </div>
      )}

      {/* hidden file input for empty state */}
      {!hasAnything && (
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files[0] || null;
            if (f) { onNewFile(f); onNewUrl(""); }
          }}
        />
      )}
    </div>
  );
}

/* ─── SummaryRow ─── */
function SummaryRow({ icon, label, value, status }) {
  const c = {
    keep:   { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534", dot: "#22c55e", badge: "محفوظ" },
    new:    { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af", dot: "#3b82f6", badge: "جديد" },
    remove: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", dot: "#ef4444", badge: "سيُحذف" },
    empty:  { bg: "#f9fafb", border: "#e5e7eb", text: "#6b7280", dot: "#d1d5db", badge: "فارغ" },
  }[status] || { bg: "#f9fafb", border: "#e5e7eb", text: "#6b7280", dot: "#d1d5db", badge: "" };

  return (
    <div className="el-sum-row" style={{ background: c.bg, border: `1.5px solid ${c.border}` }}>
      <span className="el-sum-icon">{icon}</span>
      <div className="el-sum-body">
        <span className="el-sum-label" style={{ color: c.text }}>{label}</span>
        <span className="el-sum-value" style={{ color: c.text }} title={value}>{value}</span>
      </div>
      <span className="el-sum-badge" style={{ background: c.dot + "22", color: c.text }}>
        <span className="el-sum-dot" style={{ background: c.dot }} />
        {c.badge}
      </span>
    </div>
  );
}

/* ─── Main page ─── */
export default function UpdateLesson() {
  const { id } = useParams();
  const nav    = useNavigate();

  const [fetching, setFetching] = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saveErr,  setSaveErr]  = useState("");

  /* originals from server */
  const [origPdf,      setOrigPdf]      = useState(null);
  const [origVideo,    setOrigVideo]    = useState(null);
  const [origExam,     setOrigExam]     = useState(null);
  const [origPdfUrl,   setOrigPdfUrl]   = useState("");
  const [origVideoUrl, setOrigVideoUrl] = useState("");
  const [origExamUrl,  setOrigExamUrl]  = useState("");

  /* new file replacements */
  const [newPdfFile,   setNewPdfFile]   = useState(null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  const [newExamFile,  setNewExamFile]  = useState(null);

  /* new URL replacements — null means "untouched, keep original"; "" means "explicitly cleared" */
  const [newPdfUrl,   setNewPdfUrl]   = useState(null);
  const [newVideoUrl, setNewVideoUrl] = useState(null);
  const [newExamUrl,  setNewExamUrl]  = useState(null);

  /* URL validation errors */
  const [pdfUrlErr,   setPdfUrlErr]   = useState("");
  const [videoUrlErr, setVideoUrlErr] = useState("");
  const [examUrlErr,  setExamUrlErr]  = useState("");

  /* cleared flags (tell backend to delete) */
  const [pdfCleared,   setPdfCleared]   = useState(false);
  const [videoCleared, setVideoCleared] = useState(false);
  const [examCleared,  setExamCleared]  = useState(false);

  /* file input refs */
  const pdfRef   = { current: null };
  const videoRef = { current: null };
  const examRef  = { current: null };

  useEffect(() => {
    Axios.get(ADMIN_LECTURE(id))
      .then((res) => {
        const l = res.data.lecture || res.data;
        setOrigPdf(l.pdf       || null);
        setOrigVideo(l.video   || null);
        setOrigExam(l.exam     || null);
        setOrigPdfUrl(l.pdf_url     || "");
        setOrigVideoUrl(l.video_url || "");
        setOrigExamUrl(l.exam_url   || "");
      })
      .catch(() => setSaveErr("تعذر تحميل بيانات المحاضرة"))
      .finally(() => setFetching(false));
  }, [id]);

  /* current effective display value for each slot */
  const displayPdf   = pdfCleared   ? null : (newPdfFile   ? null : origPdf);
  const displayPdfUrl   = pdfCleared   ? "" : (!newPdfFile   ? (newPdfUrl   !== null ? newPdfUrl   : origPdfUrl)   : "");
  const displayVideo = videoCleared ? null : (newVideoFile ? null : origVideo);
  const displayVideoUrl = videoCleared ? "" : (!newVideoFile ? (newVideoUrl !== null ? newVideoUrl : origVideoUrl) : "");
  const displayExam  = examCleared  ? null : (newExamFile  ? null : origExam);
  const displayExamUrl  = examCleared  ? "" : (!newExamFile  ? (newExamUrl  !== null ? newExamUrl  : origExamUrl)  : "");

  /* summary status */
  function slotStatus(cleared, newFile, newUrl, orig, origUrl) {
    if (cleared) return "remove";
    if (newFile || (newUrl !== null && newUrl !== origUrl)) return newUrl === "" ? "remove" : "new";
    if (orig || origUrl) return "keep";
    return "empty";
  }

  function summaryValue(cleared, newFile, newUrl, origUrl, origFile) {
    if (cleared) return "سيُحذف";
    if (newFile) return newFile.name;
    if (newUrl === "") return "سيُحذف";
    return (newUrl ?? origUrl) || origFile || "—";
  }

  function handlePdfUrl(v) {
    setNewPdfUrl(v);
    setPdfUrlErr(validateFileUrl(v, "PDF"));
  }
  function handleVideoUrl(v) {
    setNewVideoUrl(v);
    setVideoUrlErr(validateVideoUrl(v));
  }
  function handleExamUrl(v) {
    setNewExamUrl(v);
    setExamUrlErr(validateFileUrl(v, "الاختبار"));
  }

  async function handleSave() {
    // validate URLs before saving
    const pErr = validateFileUrl(newPdfUrl, "PDF");
    const vErr = validateVideoUrl(newVideoUrl);
    const eErr = validateFileUrl(newExamUrl, "الاختبار");
    if (pErr || vErr || eErr) {
      setPdfUrlErr(pErr);
      setVideoUrlErr(vErr);
      setExamUrlErr(eErr);
      return;
    }

    setSaving(true);
    setSaveErr("");
    try {
      const fd = new FormData();

      // PDF slot
      if (newPdfFile)            fd.append("pdf",       newPdfFile);
      else if (newPdfUrl !== null) fd.append("pdf_url",   newPdfUrl.trim());
      else if (pdfCleared)       fd.append("pdf_clear", "1");

      // Video slot
      if (newVideoFile)              fd.append("video",       newVideoFile);
      else if (newVideoUrl !== null) fd.append("video_url",   newVideoUrl.trim());
      else if (videoCleared)         fd.append("video_clear", "1");

      // Exam slot
      if (newExamFile)              fd.append("exam",       newExamFile);
      else if (newExamUrl !== null) fd.append("exam_url",   newExamUrl.trim());
      else if (examCleared)         fd.append("exam_clear", "1");

      await Axios.put(ADMIN_LECTURE(id), fd);
      nav("/dashboard/products");
    } catch (err) {
      setSaveErr(err?.response?.data?.message || "حدث خطأ أثناء الحفظ");
      setSaving(false);
    }
  }

  const hasUrlErrors = !!(pdfUrlErr || videoUrlErr || examUrlErr);

  if (fetching) {
    return (
      <div className="el-page">
        <div className="el-skeleton">
          <div className="el-sk-bar el-sk-title" />
          <div className="el-sk-bar el-sk-slot" />
          <div className="el-sk-bar el-sk-slot" />
          <div className="el-sk-bar el-sk-slot" />
        </div>
      </div>
    );
  }

  return (
    <div className="el-page">
      {/* Header */}
      <div className="el-header">
        <div className="el-header-left">
          <button className="el-back-btn" onClick={() => nav("/dashboard/products")}>
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <div>
            <h1 className="el-title">تعديل المحاضرة</h1>
            <p className="el-subtitle">رقم المحاضرة #{id}</p>
          </div>
        </div>
        <div className="el-header-actions">
          <button className="el-btn-cancel" onClick={() => nav("/dashboard/products")} disabled={saving}>إلغاء</button>
          <button className="el-btn-save" onClick={handleSave} disabled={saving || hasUrlErrors}>
            {saving ? (<><span className="el-spinner" />جاري الحفظ...</>) : (
              <><svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zm-5 16a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm3-10H5V5h10v4z" />
              </svg>حفظ التعديلات</>
            )}
          </button>
        </div>
      </div>

      {saveErr && (
        <div className="el-error-banner">
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          {saveErr}
        </div>
      )}

      <div className="el-info-card">
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" style={{ color: "#3b82f6", flexShrink: 0 }}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
        <p>استبدل كل ملف بملف جديد أو رابط، أو احذفه. اتركه كما هو إذا لم تريد التغيير.</p>
      </div>

      {/* Slots */}
      <div className="el-slots">
        <FileSlot
          icon="📄" label="ملف PDF" color="blue" accentColor="#3b82f6"
          current={displayPdf}
          currentIsUrl={false}
          newFile={newPdfFile}
          newUrl={displayPdfUrl}
          urlError={pdfUrlErr}
          onNewFile={(f) => { setNewPdfFile(f); setPdfCleared(false); }}
          onNewUrl={handlePdfUrl}
          onClear={() => { setNewPdfFile(null); setNewPdfUrl(""); setOrigPdf(null); setOrigPdfUrl(""); setPdfCleared(true); setPdfUrlErr(""); }}
          fileInputRef={pdfRef}
        />
        <FileSlot
          icon="🎬" label="ملف الفيديو" color="purple" accentColor="#8b5cf6"
          current={displayVideo}
          currentIsUrl={false}
          newFile={newVideoFile}
          newUrl={displayVideoUrl}
          urlError={videoUrlErr}
          onNewFile={(f) => { setNewVideoFile(f); setVideoCleared(false); }}
          onNewUrl={handleVideoUrl}
          onClear={() => { setNewVideoFile(null); setNewVideoUrl(""); setOrigVideo(null); setOrigVideoUrl(""); setVideoCleared(true); setVideoUrlErr(""); }}
          fileInputRef={videoRef}
        />
        <FileSlot
          icon="📝" label="ملف الاختبار" color="orange" accentColor="#f59e0b"
          current={displayExam}
          currentIsUrl={false}
          newFile={newExamFile}
          newUrl={displayExamUrl}
          urlError={examUrlErr}
          onNewFile={(f) => { setNewExamFile(f); setExamCleared(false); }}
          onNewUrl={handleExamUrl}
          onClear={() => { setNewExamFile(null); setNewExamUrl(""); setOrigExam(null); setOrigExamUrl(""); setExamCleared(true); setExamUrlErr(""); }}
          fileInputRef={examRef}
        />
      </div>

      {/* URL hints */}
      <div className="el-hints">
        <div className="el-hint-item">
          <span className="el-hint-dot" style={{ background: "#3b82f6" }} />
          <span>روابط PDF والاختبار: يجب أن تكون من <strong>Google Drive</strong></span>
        </div>
        <div className="el-hint-item">
          <span className="el-hint-dot" style={{ background: "#ef4444" }} />
          <span>روابط الفيديو: يجب أن تكون من <strong>YouTube</strong></span>
        </div>
      </div>

      {/* Summary */}
      <div className="el-summary">
        <h3 className="el-sum-title">ملخص التعديلات</h3>
        <div className="el-sum-grid">
          <SummaryRow icon="📄" label="PDF"
            value={summaryValue(pdfCleared, newPdfFile, newPdfUrl, origPdfUrl, origPdf)}
            status={slotStatus(pdfCleared, newPdfFile, newPdfUrl, origPdf, origPdfUrl)}
          />
          <SummaryRow icon="🎬" label="فيديو"
            value={summaryValue(videoCleared, newVideoFile, newVideoUrl, origVideoUrl, origVideo)}
            status={slotStatus(videoCleared, newVideoFile, newVideoUrl, origVideo, origVideoUrl)}
          />
          <SummaryRow icon="📝" label="اختبار"
            value={summaryValue(examCleared, newExamFile, newExamUrl, origExamUrl, origExam)}
            status={slotStatus(examCleared, newExamFile, newExamUrl, origExam, origExamUrl)}
          />
        </div>
      </div>

      {/* Bottom save */}
      <div className="el-footer">
        <button className="el-btn-cancel" onClick={() => nav("/dashboard/products")} disabled={saving}>إلغاء التعديل</button>
        <button className="el-btn-save" onClick={handleSave} disabled={saving || hasUrlErrors}>
          {saving ? "جاري الحفظ..." : "حفظ التعديلات"}
        </button>
      </div>
    </div>
  );
}
